import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { createLogger } from '../../../../utils/logger';
const logger = createLogger('MemorialServiceData');

import {
  MemorialServiceInfoRec,
  memorialServiceInfoService,
  ChapelDetails,
  ChurchDetails,
  CemeteryDetails,
  ResidenceDetails,
  OutdoorDetails,
  OtherDetails,
} from '../../../../services/memorialServiceInfo';

type MemorialServiceDataScreenRouteProp = RouteProp<RootStackParamList, 'MemorialServiceData'>;

type RouteParams = {
  taskId: number;
  targetUserId?: string;
  viewOnly?: boolean;
  memorialServiceId?: number;
};

type InfoItemProps = {
  label: string;
  value: string | boolean | undefined;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => {
  let displayValue = '';

  if (typeof value === 'boolean') {
    displayValue = value ? 'Yes' : 'No';
  } else if (typeof value === 'string') {
    displayValue = value || 'Not specified';
  } else {
    displayValue = 'Not specified';
  }

  return (
    <View style={styles.infoItem}>
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.value}>{displayValue}</Text>
      <View style={styles.separator} />
    </View>
  );
};

export const MemorialServiceDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<MemorialServiceDataScreenRouteProp>();
  const { targetUserId, viewOnly, memorialServiceId } = (route.params as RouteParams) || {};
  const [memorialServiceInfo, setMemorialServiceInfo] = useState<MemorialServiceInfoRec | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMemorialServiceInfo();
  }, []);

  const loadMemorialServiceInfo = async () => {
    try {
      if (memorialServiceId) {
        const info = await memorialServiceInfoService.getMemorialServiceInfoById(memorialServiceId, targetUserId);
        setMemorialServiceInfo(info);
      } else {
        // Load the first/latest entry if no specific ID provided
        const allInfo = await memorialServiceInfoService.getAllMemorialServiceInfo(targetUserId);
        if (allInfo.length > 0) {
          setMemorialServiceInfo(allInfo[0]);
        }
      }
    } catch (error) {
      logger.error('Error loading memorial service info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (memorialServiceInfo) {
      // Map the data from memorialServiceInfo to the format expected by MemorialServiceInputScreen
      const mappedData = {
        id: memorialServiceInfo.id,
        arrangementType: memorialServiceInfo.serviceType, // serviceType -> arrangementType
        serviceDetails: memorialServiceInfo.serviceDetails,
        specialInstructions: memorialServiceInfo.specificInstructions, // specificInstructions -> specialInstructions
        notes: memorialServiceInfo.notes,
        estimatedCost: memorialServiceInfo.estimatedCost,
        preferredLocation: memorialServiceInfo.preferredLocation,
        contactName: memorialServiceInfo.contactName,
        contactPhone: memorialServiceInfo.contactPhone,
      };

      navigation.navigate('MemorialServiceInput', {
        memorialServiceData: mappedData,
        isEditing: true
      });
    } else {
      navigation.navigate('MemorialServiceInput');
    }
  };

  const handleDelete = () => {
    if (memorialServiceInfo) {
      Alert.alert(
        'Delete Memorial Service',
        'Are you sure you want to delete this memorial service information? This action cannot be undone.',
        [
          {
            text: 'Cancel',
            style: 'cancel',
          },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              try {
                await memorialServiceInfoService.deleteMemorialServiceInfo(memorialServiceInfo.id);
                navigation.navigate('FinalWishesChecklist');
              } catch (error) {
                logger.error('Error deleting memorial service info:', error);
                Alert.alert('Error', 'Failed to delete memorial service information');
              }
            },
          },
        ]
      );
    }
  };

  const handleBack = () => {
    navigation.navigate('FinalWishesChecklist');
  };

  const renderServiceDetails = () => {
    if (!memorialServiceInfo || !memorialServiceInfo.serviceDetails) {
      return null;
    }

    const details = memorialServiceInfo.serviceDetails;
    const serviceType = memorialServiceInfo.serviceType;

    switch (serviceType) {
      case 'chapel':
        const chapelDetails = details as ChapelDetails;
        return (
          <View>
            <Text style={styles.sectionTitle}>Chapel Service Details</Text>
            <InfoItem label="Live Streaming Service" value={chapelDetails.liveStreamingService} />
            <InfoItem label="Viewing Before Service" value={chapelDetails.viewingBeforeService} />
            <InfoItem label="Reception After" value={chapelDetails.receptionAfter} />
            <InfoItem label="Specific Requests" value={chapelDetails.specificRequests} />
            <InfoItem label="Music Preferences" value={chapelDetails.musicPreferences} />
          </View>
        );

      case 'church':
        const churchDetails = details as ChurchDetails;
        return (
          <View>
            <Text style={styles.sectionTitle}>Church Service Details</Text>
            <InfoItem label="Type of Music" value={churchDetails.typeOfMusic} />
            <InfoItem label="Specific Clergy" value={churchDetails.specificClergy} />
            <InfoItem label="Communion" value={churchDetails.communion} />
            <InfoItem label="Religious Readings" value={churchDetails.religiousReadings} />
            <InfoItem label="Denomination Requirements" value={churchDetails.denominationRequirements} />
          </View>
        );

      case 'cemetery':
      case 'cemetary':
        const cemeteryDetails = details as CemeteryDetails;
        return (
          <View>
            <Text style={styles.sectionTitle}>Cemetery Service Details</Text>
            <InfoItem label="Outdoor Tent" value={cemeteryDetails.outdoorTent} />
            <InfoItem label="Chairs Provided" value={cemeteryDetails.chairsProvided} />
            <InfoItem label="Type of Ceremony" value={cemeteryDetails.typeOfCeremony} />
            <InfoItem label="Weather Contingency" value={cemeteryDetails.weatherContingency} />
            <InfoItem label="Graveside Music" value={cemeteryDetails.gravesideMusic} />
          </View>
        );

      case 'residence':
        const residenceDetails = details as ResidenceDetails;
        return (
          <View>
            <Text style={styles.sectionTitle}>Residence Service Details</Text>
            <InfoItem label="Home Location" value={residenceDetails.homeLocation} />
            <InfoItem label="Max Guest Count" value={residenceDetails.maxGuestCount} />
            <InfoItem label="Catering Needs" value={residenceDetails.cateringNeeds} />
            <InfoItem label="Parking Arrangements" value={residenceDetails.parkingArrangements} />
            <InfoItem label="Sound System Needed" value={residenceDetails.soundSystemNeeded} />
          </View>
        );

      case 'outdoor':
      case 'outdoors':
        const outdoorDetails = details as OutdoorDetails;
        return (
          <View>
            <Text style={styles.sectionTitle}>Outdoor Service Details</Text>
            <InfoItem label="Specific Location" value={outdoorDetails.specificLocation} />
            <InfoItem label="Permit Required" value={outdoorDetails.permitRequired} />
            <InfoItem label="Weather Backup Plan" value={outdoorDetails.weatherBackupPlan} />
            <InfoItem label="Seating Arrangements" value={outdoorDetails.seatingArrangements} />
            <InfoItem label="Accessibility Considerations" value={outdoorDetails.accessibilityConsiderations} />
          </View>
        );

      case 'other':
        const otherDetails = details as OtherDetails;
        return (
          <View>
            <Text style={styles.sectionTitle}>Other Service Details</Text>
            <InfoItem label="Custom Description" value={otherDetails.customDescription} />
            <InfoItem label="Special Venue" value={otherDetails.specialVenue} />
            <InfoItem label="Unique Requirements" value={otherDetails.uniqueRequirements} />
            <InfoItem label="Logistical Needs" value={otherDetails.logisticalNeeds} />
          </View>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>
        <Text style={styles.title}>Memorial Service Details</Text>
        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>Loading...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Memorial Service Details</Text>
      <View style={styles.whiteContainer}>
        {viewOnly && (
          <View style={styles.viewOnlyBanner}>
            <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
          </View>
        )}
        <ScrollView style={styles.scrollView}>
          {memorialServiceInfo ? (
            <>
              <InfoItem
                label="Service Type"
                value={memorialServiceInfo.serviceType ?
                  memorialServiceInfo.serviceType.charAt(0).toUpperCase() +
                  memorialServiceInfo.serviceType.slice(1) : 'Not specified'}
              />

              {memorialServiceInfo.specificInstructions && (
                <InfoItem label="Specific Instructions" value={memorialServiceInfo.specificInstructions} />
              )}

              {memorialServiceInfo.preferredLocation && (
                <InfoItem label="Preferred Location" value={memorialServiceInfo.preferredLocation} />
              )}

              {memorialServiceInfo.contactName && (
                <InfoItem label="Contact Name" value={memorialServiceInfo.contactName} />
              )}

              {memorialServiceInfo.contactPhone && (
                <InfoItem label="Contact Phone" value={memorialServiceInfo.contactPhone} />
              )}

              {memorialServiceInfo.estimatedCost && (
                <InfoItem label="Estimated Cost" value={`$${memorialServiceInfo.estimatedCost.toLocaleString()}`} />
              )}

              {memorialServiceInfo.notes && (
                <InfoItem label="Notes" value={memorialServiceInfo.notes} />
              )}

              {renderServiceDetails()}

              <View style={styles.buttonContainer}>
                <TouchableOpacity
                  style={[styles.button, styles.backToListButton]}
                  onPress={handleBack}
                >
                  <Text style={styles.buttonText}>Back</Text>
                </TouchableOpacity>

                {!viewOnly && (
                  <>
                    <TouchableOpacity
                      style={[styles.button, styles.deleteButton]}
                      onPress={handleDelete}
                    >
                      <Text style={styles.buttonText}>Delete</Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[styles.button, styles.editButton]}
                      onPress={handleEdit}
                    >
                      <Text style={styles.buttonText}>Edit</Text>
                    </TouchableOpacity>
                  </>
                )}
              </View>
            </>
          ) : (
            <View style={styles.noDataContainer}>
              <Text style={styles.noDataText}>No memorial service information found.</Text>
              {!viewOnly && (
                <TouchableOpacity
                  style={[styles.button, styles.editButton, { marginTop: 20 }]}
                  onPress={handleEdit}
                >
                  <Text style={styles.buttonText}>Add Memorial Service</Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  infoItem: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
    marginBottom: ms(8),
  },
  value: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  separator: {
    height: s(2),
    backgroundColor: '#29BFC0',
    marginTop: ms(8),
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: ms(20),
    paddingBottom: ms(130),
    gap: ms(10),
  },
  button: {
    flex: 1,
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  deleteButton: {
    backgroundColor: '#FF4444',
  },
  editButton: {
    backgroundColor: '#FF9F5A',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
  },
  noDataContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  noDataText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
  },
  sectionTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginTop: ms(20),
    marginBottom: ms(15),
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});

export default MemorialServiceDataScreen;
